import { useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router-dom'

import { db, lireMeta, META_DERNIERE_SYNCHRO } from '../../db/db'
import { ecrireReglages, lireReglages } from '../../db/repo'
import {
  effacerTout,
  exporter,
  importer,
  importerTampon,
  nomFichierExport,
} from '../../db/sauvegarde'
import { formatDateHeure, relatif } from '../../domain/time'
import type { PreferenceFin } from '../../domain/types'
import { lireQuota } from '../../pwa/register'
import { Carte, Champ, Confirmation, Encart, Entete, useAsync } from '../composants'

const PREFERENCES: { valeur: PreferenceFin; libelle: string }[] = [
  { valeur: 'premier', libelle: 'Le premier signal du matin' },
  { valeur: 'alarme', libelle: 'L’arrêt de l’alarme' },
  { valeur: 'debranche', libelle: 'Le débranchement du chargeur' },
]

export function PageReglages() {
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [effacement, setEffacement] = useState(false)

  const donnees = useLiveQuery(async () => {
    const [reglages, evenements, nuits, derniereSynchro] = await Promise.all([
      lireReglages(),
      db.evenements.count(),
      db.nuits.count(),
      lireMeta<string | null>(META_DERNIERE_SYNCHRO, null),
    ])
    return { reglages, evenements, nuits, derniereSynchro }
  }, [])

  const quota = useAsync(() => lireQuota(), [])

  if (!donnees) {
    return (
      <>
        <Entete titre="Réglages" />
        <main className="contenu">
          <p className="doux">Chargement…</p>
        </main>
      </>
    )
  }

  const r = donnees.reglages

  const nombre = (cle: keyof typeof r, valeur: string) => {
    const n = Number(valeur)
    if (!Number.isFinite(n)) return
    void ecrireReglages({ [cle]: n } as never)
  }

  return (
    <>
      <Entete titre="Réglages" />
      <main className="contenu">
        {message && <Encart ton="ok">{message}</Encart>}
        {erreur && <Encart ton="danger">{erreur}</Encart>}

        <Carte titre="Objectif">
          <Champ libelle="Temps posé visé, en heures">
            <input
              type="number"
              min={4}
              max={12}
              step={0.5}
              value={r.objectifMinutes / 60}
              onChange={(e) => nombre('objectifMinutes', String(Number(e.target.value) * 60))}
            />
          </Champ>
        </Carte>

        <Carte titre="Découpage des nuits">
          <div className="pile">
            <Champ
              libelle="Fin de nuit retenue"
              aide="Le premier signal du matin est le plus proche du réveil réel : débrancher peut arriver bien plus tard."
            >
              <select
                value={r.preferenceFin}
                onChange={(e) => void ecrireReglages({ preferenceFin: e.target.value as PreferenceFin })}
              >
                {PREFERENCES.map((p) => (
                  <option key={p.valeur} value={p.valeur}>
                    {p.libelle}
                  </option>
                ))}
              </select>
            </Champ>

            <Champ
              libelle="Silence minimal marquant le coucher (min)"
              aide="Le coucher retenu est le premier moment suivi d’un silence au moins aussi long. Plus bas, un coup d’œil tardif décale moins le coucher ; plus haut, une soirée entrecoupée reste d’un seul tenant."
            >
              <input
                type="number"
                min={20}
                max={240}
                step={5}
                value={r.seuilNuitMinutes}
                onChange={(e) => nombre('seuilNuitMinutes', e.target.value)}
              />
            </Champ>

            <Champ
              libelle="Fusion de deux usages proches (min)"
              aide="En dessous de cet écart, deux consultations comptent pour un seul réveil."
            >
              <input
                type="number"
                min={1}
                max={90}
                step={1}
                value={r.seuilFusionMinutes}
                onChange={(e) => nombre('seuilFusionMinutes', e.target.value)}
              />
            </Champ>

            <Champ
              libelle="Durée en deçà de laquelle un usage est ignoré (min)"
              aide="Consulter l’heure n’est pas un réveil. Ce n’est qu’un défaut : chaque interruption reste arbitrable."
            >
              <input
                type="number"
                min={0}
                max={30}
                step={1}
                value={r.seuilReveilMinutes}
                onChange={(e) => nombre('seuilReveilMinutes', e.target.value)}
              />
            </Champ>
          </div>
          <p className="tres-doux" style={{ marginTop: 10 }}>
            Modifier un seuil rejoue le découpage sur tout l’historique. Les nuits corrigées
            à la main ne sont jamais recalculées.
          </p>
        </Carte>

        <Carte titre="Relevés">
          <div className="pile pile--serree">
            <div className="ligne ligne--espacee">
              <span className="doux">Relevés bruts</span>
              <span className="mono">{donnees.evenements}</span>
            </div>
            <div className="ligne ligne--espacee">
              <span className="doux">Nuits</span>
              <span className="mono">{donnees.nuits}</span>
            </div>
            <div className="ligne ligne--espacee">
              <span className="doux">Dernière synchronisation</span>
              <span className="mono">
                {donnees.derniereSynchro ? relatif(donnees.derniereSynchro) : '—'}
              </span>
            </div>
            {quota.valeur && (
              <div className="ligne ligne--espacee">
                <span className="doux">Stockage</span>
                <span className="mono">
                  {(quota.valeur.utiliseOctets / 1024).toFixed(0)} Ko
                  {quota.valeur.persistant ? ' · persistant' : ''}
                </span>
              </div>
            )}
          </div>
          <p style={{ marginTop: 12 }}>
            <Link className="btn btn--bloc" to="/installation">
              Guide des raccourcis
            </Link>
          </p>
        </Carte>

        <ImportTampon onMessage={setMessage} onErreur={setErreur} />

        <Sauvegardes onMessage={setMessage} onErreur={setErreur} />

        <Carte titre="Tout effacer">
          <p className="doux">
            Irréversible. Aucun serveur ne détient de copie : sans sauvegarde exportée, les
            nuits sont définitivement perdues.
          </p>
          <button
            type="button"
            className="btn btn--danger btn--bloc"
            style={{ marginTop: 12 }}
            onClick={() => setEffacement(true)}
          >
            Effacer toutes les données
          </button>
        </Carte>

        {effacement && (
          <Confirmation
            titre="Tout effacer ?"
            message="Relevés, nuits et réglages seront supprimés de cet appareil."
            libelleConfirmer="Effacer"
            danger
            onConfirmer={() => {
              setEffacement(false)
              void effacerTout().then(() => setMessage('Données effacées.'))
            }}
            onAnnuler={() => setEffacement(false)}
          />
        )}
      </main>
    </>
  )
}

// ---------------------------------------------------------------------------

/**
 * Rattrapage manuel du fichier tampon.
 *
 * Le filet quand la synchro du matin a echoue : URL rejetee, telephone repose
 * verrouille. Volontairement manuel — l'automatiser demanderait un serveur, et
 * ce chemin ne devrait servir que deux fois par an.
 */
function ImportTampon({
  onMessage,
  onErreur,
}: {
  onMessage: (m: string) => void
  onErreur: (m: string) => void
}) {
  const champ = useRef<HTMLInputElement>(null)

  return (
    <Carte titre="Rattraper un relevé manqué">
      <p className="doux">
        Si l’ouverture automatique du matin a échoué, les relevés sont toujours dans{' '}
        <code>historique.txt</code> sur l’iPhone. Ce bouton ouvre l’app Fichiers pour l’y
        lire. Les relevés déjà connus sont ignorés.
      </p>
      <input
        ref={champ}
        type="file"
        accept=".txt,text/plain"
        style={{ display: 'none' }}
        onChange={(e) => {
          const fichier = e.target.files?.[0]
          if (!fichier) return
          fichier
            .text()
            .then(importerTampon)
            .then((r) => onMessage(`${r.evenements} relevé(s) ajouté(s).`))
            .catch((err: unknown) =>
              onErreur(err instanceof Error ? err.message : 'Fichier illisible.'),
            )
            .finally(() => {
              if (champ.current) champ.current.value = ''
            })
        }}
      />
      <button
        type="button"
        className="btn btn--bloc"
        style={{ marginTop: 12 }}
        onClick={() => champ.current?.click()}
      >
        Choisir un fichier de relevés
      </button>
    </Carte>
  )
}

function Sauvegardes({
  onMessage,
  onErreur,
}: {
  onMessage: (m: string) => void
  onErreur: (m: string) => void
}) {
  const champ = useRef<HTMLInputElement>(null)

  const telecharger = async () => {
    const data = await exporter()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = nomFichierExport()
    a.click()
    URL.revokeObjectURL(url)
    onMessage(`Sauvegarde exportée le ${formatDateHeure(data.exporteLe)}.`)
  }

  return (
    <Carte titre="Sauvegarde">
      <p className="doux">
        Les données ne quittent l’appareil que par ce fichier. C’est la seule protection
        contre la perte de l’iPhone.
      </p>
      <input
        ref={champ}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const fichier = e.target.files?.[0]
          if (!fichier) return
          fichier
            .text()
            .then((t) => importer(JSON.parse(t) as unknown))
            .then((r) => onMessage(`${r.evenements} relevé(s) et ${r.nuits} nuit(s) importés.`))
            .catch((err: unknown) =>
              onErreur(err instanceof Error ? err.message : 'Fichier illisible.'),
            )
            .finally(() => {
              if (champ.current) champ.current.value = ''
            })
        }}
      />
      <div className="boutons" style={{ marginTop: 12 }}>
        <button type="button" className="btn grandir" onClick={() => void telecharger()}>
          Exporter
        </button>
        <button type="button" className="btn grandir" onClick={() => champ.current?.click()}>
          Importer
        </button>
      </div>
    </Carte>
  )
}
